import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { staffApi } from './staffApi';

interface Row {
  id: string;
  name: string;
  mode: string;
  trainerId: string | null;
  trainerLocked: boolean;
  ruleLocked: boolean;
  validTo: string | null;
  coaches: Array<{ id: string; name: string; level: string | null }>;
}

/**
 * The coach on each of a member's trainer plans / coach-included plans.
 * "Gym assigns" offerings get their coach here; a member's chosen coach can
 * be changed here too (sessions then go to the new coach).
 */
export default function EntitlementCoaches({ memberId, readOnly, onChanged }: { memberId: string; readOnly: boolean; onChanged?: () => void }) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => staffApi<Row[]>(`/member-entitlements?memberId=${memberId}`).then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, [memberId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!rows || rows.length === 0) return null;

  const assign = async (r: Row, trainerId: string, acceptClashes = false) => {
    setBusy(r.id);
    try {
      const res = await staffApi<{ standingMoved?: number }>(`/entitlements/${r.id}/trainer`, { method: 'PUT', body: { trainerId: trainerId || null, acceptClashes } });
      toast.success(res?.standingMoved ? `Coach changed — ${res.standingMoved} weekly slot${res.standingMoved === 1 ? '' : 's'} moved` : trainerId ? 'Coach assigned' : 'Coach removed');
      await load();
      onChanged?.();
    } catch (e: any) {
      // Weekly slots move with the coach — checked first. Staff may keep the
      // times anyway: the clashing dates are then skipped, visibly.
      if (e.code === 'SLOT_CONFLICT' && window.confirm(`${e.message}\n\nChange the coach anyway and skip those dates?`)) {
        setBusy(null);
        return assign(r, trainerId, true);
      }
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-xl border border-lime-200 bg-lime-50/50 p-3 space-y-2">
      <p className="text-[10px] font-black uppercase tracking-widest text-lime-800">Coach on training</p>
      {rows.map(r => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">{r.name}</p>
            <p className="text-[11px] text-gray-500">
              {r.mode === 'member_picks' ? 'Member chose' : 'Gym assigns'}
              {r.trainerLocked ? ' · sessions only with this coach' : ''}
              {!r.trainerId && r.mode === 'gym_assigns' ? ' · not assigned yet' : ''}
            </p>
          </div>
          <select
            value={r.trainerId ?? ''}
            disabled={readOnly || busy === r.id}
            onChange={e => assign(r, e.target.value)}
            className={`px-3 py-2 rounded-lg border text-sm bg-white ${!r.trainerId && r.mode === 'gym_assigns' ? 'border-amber-400' : 'border-gray-200'}`}
          >
            <option value="">{r.ruleLocked ? 'Choose a coach…' : 'Any available'}</option>
            {r.coaches.map(c => <option key={c.id} value={c.id}>{c.name}{c.level ? ` · ${c.level}` : ''}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
}
